import React from 'react'
import { connect } from 'react-redux'
import injectSheet from 'react-jss'
import style from './style'
import { updateUiAction } from 'actions/uiActions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DEFAULT_WALL_OPTIONS } from 'helpers/globals'
import { updateCurrentEntityAction } from 'actions/applicationActions'
import MeasurementLineOverlay from 'components/MapComponents/MeasurementLineOverlay'
import getMapProjection from 'helpers/map/getMapProjection'
import { distanceSquared, DOUBLE_CLICK_DISTANCE } from 'helpers/walls/snapToWall'
import ContextMenu from 'ContextMenu'
import showContextMenu from 'helpers/showContextMenu'
import ReactDOMServer from 'react-dom/server'
import convertUnitFromMap from 'helpers/convertUnitFromMap'
import Clipboard from 'clipboard'

const mapStateToProps = state => {
  const { map, exportType, exports, ui: { measurementTool, drawingMeasurement, isImperial }, currentEntity: { type } } = state

  return {
    map: map.ref,
    exportType,
    exporting: !EXPORT && !!exports.length,
    measurementTool,
    currentEntityType: type,
    drawingMeasurement,
    isImperial
  }
}

class MeasurementDetailsPanel extends React.Component {
  state = { distance: 0 }
  constructor(props) {
    super(props)
    this.measurementLine = null
    this.measurementLineOverlay = null
    this.markups = []
    this.contextMenu = new ContextMenu(props.map, this.contextMenuOptions())
    this.mapContainer = null

    this.moveListener = null
    this.checkDoubleClick = null
    this.clickListener = null
    this.downListener = null
    this.skipEventListener = null
    this.dragListener = null
  }

  cancel = (clearLine = true) => {
    const { mapContainer, moveListener, checkDoubleClick, clickListener, downListener,
      skipEventListener, dragListener, markups, measurementLineOverlay, measurementLine } = this

    mapContainer.removeEventListener('mousemove', moveListener)
    mapContainer.removeEventListener('click', checkDoubleClick, true)
    mapContainer.removeEventListener('click', clickListener)
    mapContainer.removeEventListener('mousedown', downListener)
    mapContainer.removeEventListener('touchstart', downListener)
    mapContainer.removeEventListener('rightclick', skipEventListener, true)
    mapContainer.removeEventListener('contextmenu', skipEventListener, true)

    google.maps.event.removeListener(dragListener)
    markups.forEach(m => m.setMap(null))

    if (clearLine) measurementLine.setMap(null)

    if (measurementLineOverlay) {
      measurementLineOverlay.setMap(null)
      this.measurementLineOverlay = null
    }
  }

  activateMeasurementToolMode = _ => {
    const { map, updateUiAction, isImperial, currentEntityType, classes } = this.props

    const iconSymbol = {
      path: 'M9.35,32.8V3 M0,3h18.7 M18.7,32.8H0',
      anchor: new google.maps.Point(9, 17.9),
      scale: 0.7,
      strokeWeight: 2.6,
      strokeColor: '#000000',
      rotation: 90
    }

    const mouseFollower = new google.maps.Marker({
      icon: { ...iconSymbol, rotation: 0 },
      position: map.getCenter(),
      visible: false,
      map
    })
    let followerShown = false

    let icons = [{ icon: iconSymbol, offset: '0%' }, { icon: iconSymbol, offset: '100%' }]

    this.measurementLine = new google.maps.Polyline({
      ...DEFAULT_WALL_OPTIONS,
      strokeColor: '#000000',
      path: [],
      map,
      icons: icons.slice(0, 1)
    })

    const computeTotalLength = e => {
      if (this.state.dragging) return
      let distance = path.getArray().reduce((sum, point, index, path) => index !== 0 ? sum + google.maps.geometry.spherical.computeDistanceBetween(point, path[index - 1]) : sum, 0)
      distance = (convertUnitFromMap({ value: distance, isImperial })).toFixed(0)
      this.setState({ distance })
      let distanceOutput = this.state.distance + ' ' + (isImperial ? 'ft' : 'm')
      this.measurementLineOverlay && this.measurementLineOverlay.updateDistance(distanceOutput)
    }

    let path = this.measurementLine.getPath()
    google.maps.event.addListener(path, 'set_at', computeTotalLength)
    google.maps.event.addListener(path, 'insert_at', computeTotalLength)
    google.maps.event.addListener(path, 'remove_at', computeTotalLength)

    let followerPath = []
    const followerMeasurementLine = new google.maps.Polyline({
      ...DEFAULT_WALL_OPTIONS,
      strokeColor: '#000000',
      editable: false,
      draggable: false,
      strokeOpacity: 0.55,
      followerPath,
      map,
      icons
    })

    this.mapContainer = document.getElementById('mapContainer')
    const mapComponentRef = this.mapContainer.children[0]
    const zoomButton = document.querySelector('.gm-bundled-control')
    const projection = getMapProjection(map)

    const complete = _ => {
      this.cancel(false)

      google.maps.event.removeListener(measurementToolListener)

      this.measurementLine.addListener('contextmenu', showContextMenu(this.contextMenu))
      google.maps.event.addListener(this.contextMenu, 'menu_item_selected', this.eventHandler)
      this.measurementLineOverlay = new MeasurementLineOverlay({
        map,
        polyline: this.measurementLine,
        containerClassName: classes.MeasurementLineOverlay,
        distance: this.state.distance + ' ' + (isImperial ? 'ft' : 'm'),
        path
      })

      updateUiAction({ drawingMeasurement: false })
      this.measurementLine.setOptions({ icons })
    }

    let moveListenerFrame
    const handleMove = e => {
      const bounds = this.mapContainer.getBoundingClientRect()
      const relativePixel = { x: e.clientX - bounds.left, y: e.clientY - bounds.top }
      const latlngPos = projection.fromContainerPixelToLatLng(relativePixel)

      if (path.getLength() > 0) {
        followerPath[1] = latlngPos
        followerMeasurementLine.setPath(followerPath)
      } else {
        mouseFollower.setPosition(latlngPos)
        if (!followerShown) {
          followerShown = true
          mouseFollower.setVisible(true)
        }
      }
    }

    this.moveListener = e => {
      if (moveListenerFrame) cancelAnimationFrame(moveListenerFrame)
      handleMove(e)
      moveListenerFrame = requestAnimationFrame(_ => {
        moveListenerFrame = null
      })
    }

    this.checkDoubleClick = e => {
      if (!mapComponentRef.contains(e.target)) return e.stopPropagation()
      if (skipClick || zoomButton.contains(e.target)) return

      const bounds = this.mapContainer.getBoundingClientRect()
      const pixelPos = { x: e.clientX - bounds.left, y: e.clientY - bounds.top }
      const prevPixelPos = projection.fromLatLngToContainerPixel(path.getAt(path.getLength() - 1))

      if (path.getLength() > 0 && Math.sqrt(distanceSquared(pixelPos.x, pixelPos.y, prevPixelPos.x, prevPixelPos.y)) < DOUBLE_CLICK_DISTANCE) {
        e.stopPropagation()
        complete()
      }
    }

    this.clickListener = e => {
      if (skipClick || !mapComponentRef.contains(e.target) || zoomButton.contains(e.target)) return

      const bounds = this.mapContainer.getBoundingClientRect()
      const relativePixel = { x: e.clientX - bounds.left, y: e.clientY - bounds.top }
      const latlngPos = projection.fromContainerPixelToLatLng(relativePixel)

      path.push(latlngPos)
      followerPath = [latlngPos, latlngPos]
      followerMeasurementLine.setPath(followerPath)

      path.getLength() === 2 && followerMeasurementLine.setOptions({ icons: icons.slice(1) })
      mouseFollower.setVisible(false)
    }

    let skipClick = false
    this.downListener = e => {
      if (!mapComponentRef.contains(e.target)) return
      skipClick = false
    }

    this.skipEventListener = e => e.stopPropagation()

    this.mapContainer.addEventListener('mousemove', this.moveListener)
    this.mapContainer.addEventListener('click', this.checkDoubleClick, true)
    this.mapContainer.addEventListener('click', this.clickListener)
    this.mapContainer.addEventListener('mousedown', this.downListener)
    this.mapContainer.addEventListener('touchstart', this.downListener)
    this.mapContainer.addEventListener('rightclick', this.skipEventListener, true)
    this.mapContainer.addEventListener('contextmenu', this.skipEventListener, true)

    const measurementToolListener = google.maps.event.addListener(this.measurementLine, 'click', e => {
      if (e.vertex != 0) return
      path.push(path.getAt(0))
      complete()
    })

    this.dragListener = google.maps.event.addListener(map, 'dragstart', e => skipClick = true)

    if (currentEntityType !== 'map' && currentEntityType !== 'camera') updateCurrentEntityAction({ type: 'map' })

    google.maps.event.addListener(this.measurementLine, 'dragstart', _ => this.setState({ dragging: true }))
    google.maps.event.addListener(this.measurementLine, 'dragend', _ => this.setState({ dragging: false }))

    this.markups = [mouseFollower, followerMeasurementLine]
  }

  componentDidMount() {
    this.activateMeasurementToolMode()
  }

  contextMenuOptions = _ => {
    const menuItems = []

    const menuItemBuilder = function() {
      const { classes } = this.props
      return menuItems.concat([
        {
          className: classes.contextMenuItem,
          eventName: 'cancelMeasurementTool',
          id: 'menu_item_cancel_measurement_tool',
          label: `${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon={['far', 'trash-alt']} />)} Cancel Measurement`
        },
        {
          className: classes.contextMenuItem,
          eventName: 'copyMeasurement',
          id: 'menu_item_copy_measurement',
          label: `${ReactDOMServer.renderToStaticMarkup(<FontAwesomeIcon icon='clipboard-list' />)} Copy Measurement`
        }
      ])
    }

    return {
      classNames: {
        menu: this.props.classes.contextMenu,
        menuSeparator: this.props.classes.contextMenuSeparator
      },
      menuItems: menuItemBuilder.bind(this)
    }
  }

  eventHandler = (latLng, eventName) => {
    const { props: { mapContextMenuEventHandler, isImperial, updateUiAction } } = this
    const mapContextMenuEvents = ['addCamHere', 'centerMapHere', 'zoomToAllDevices']
    if (mapContextMenuEvents.includes(eventName)) {
      mapContextMenuEventHandler(latLng, eventName)
    } else {
      switch (eventName) {
        case 'cancelMeasurementTool':
          updateUiAction({ measurementTool: false, drawingMeasurement: false })
          this.cancel()
          break
        case 'copyMeasurement':
          navigator.clipboard.writeText(this.state.distance + ' ' + (isImperial ? 'ft' : 'm'))
          break
      }
    }
    this.contextMenu.hide()
  }

  componentWillUnmount = _ => {
    this.cancel()
  }

  componentDidUpdate() {
    if (this.state.distance) new Clipboard(`.${this.props.classes.copyButton}`)
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.drawingMeasurement && nextProps.drawingMeasurement) {
      this.setState({ distance: 0 })
      this.cancel()
      this.activateMeasurementToolMode()
    }
  }

  render() {
    const { props: { measurementTool, isImperial, updateUiAction, classes }, state: { distance } } = this

    if (!measurementTool) return null

    return (
      <div className={classes.instructions}>
        <div className={classes.topUiControls}>
          <a onClick={_ => {
            updateUiAction({ measurementTool: false, drawingMeasurement: false })
            this.cancel()
          }}>
            <FontAwesomeIcon icon='caret-left' className={classes.goBackButton} />
            All Project Items
          </a>
        </div>
        <div className={classes.measurementText}>
            Measurement
        </div>
        <div className={classes.measurementDisplayText}>
          <span id='measurementText'>{ ' ' + distance + ' ' + (isImperial ? 'ft' : 'm') }</span>
          <div className={classes.copyButton} data-clipboard-target={'#measurementText'}>
            Copy
            <FontAwesomeIcon icon='clipboard-list' className={classes.clipboardButton} />
          </div>
          <div className={classes.cancelButton} onClick={_ => {
            updateUiAction({ measurementTool: false, drawingMeasurement: false })
            this.cancel()
          }}>
              Cancel
            <FontAwesomeIcon icon='ban' className={classes.cancelButtonIcon} />
          </div>
        </div>
      </div>
    )
  }
}

export default connect(mapStateToProps, { updateUiAction, updateCurrentEntityAction })(injectSheet(style)(MeasurementDetailsPanel))



// WEBPACK FOOTER //
// ./src/components/MeasurementDetailsPanel/index.js